<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="info"
        fab
        title="Mass Edit"
        v-on="on"
      >
        <v-icon medium>
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      :loading="loading"
      color="primary"
      style="margin-bottom:0"
    >
      <template v-slot:header>
        <v-card-title>
          <div class="px-2">
            <h4 class="title font-weight-light">
              Company Quota
            </h4>
            <p class="category font-weight-thin">
              Mass Edit Company Quota
            </p>
          </div>
          <v-spacer />
          <v-btn  
            text
            icon 
            color="white"
            @click="dialog = false"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
      </template>
      <v-card
        v-show="searched"
        class="pl-6 pt-6"
      >      
        <v-row
          class="title"
        >
          <span>{{ companyName }}</span>
        </v-row>
        <v-row
          class="title"
        >
          <span> Year: {{ year }}</span>
        </v-row>
        <v-row
          class="title"
        >
          <span> Category: {{ quotaCategory }}</span>
        </v-row>
      </v-card>
      <v-form
        v-if="dialog && !loading"
      >
        <v-container>
          <div v-if="!searched">
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="companyId"
                  v-validate="'required'"
                  item-text="name"
                  item-value="id"
                  :items="companies"
                  :error-messages="errors.collect('company')"
                  data-vv-name="company"  
                  label="Company"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="year"
                  v-validate="'required'"
                  :items="years"
                  :error-messages="errors.collect('year')"
                  data-vv-name="year"  
                  label="Year"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="quotaCategory"
                  item-text="value"
                  item-value="id"
                  :items="categories"
                  data-vv-name="category"  
                  label="Category"
                >
                  <template v-slot:append-outer>  
                    <v-btn
                      text
                      icon
                      title="Remove Category"
                      color="blue lighten-2"
                      @click="quotaCategory = ''"
                    >
                      <v-icon
                        v-text="'mdi-eraser'"
                      />
                    </v-btn>
                  </template>
                </v-select>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-spacer />
              <v-btn
                :loading="progressing"
                color="success"
                @click="createAndEdit"
              >
                Search
              </v-btn>
            </v-card-actions>
          </div>
          <div
            v-else
            class="overflow"
            data-simplebar
          >
            <div
              v-for="(quotas, index) in localCompanyQuota"
              :key="index"
            >
              <v-row>
                <v-col>
                  <v-text-field
                    v-model="localCompanyQuota[index].quota"
                    v-validate="{
                      required: true,
                      decimal:2,
                      min_value:quotas.quota_used,
                      quotaLimit: localCompanyQuota[index]
                    }"
                    type="number"
                    :label="months(quotas.month)"
                    :error-messages="errors.collect(`quota${index}`)"
                    :name="`quota${index}`"
                    :disabled="isPast(quotas.date)"
                    data-vv-as="quota"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    :value="Number(quotas.carry_forward)"
                    disabled
                    label="Carry Forward"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    :value="quotas.quota_used"
                    disabled
                    label="Quota Used"
                  />
                </v-col>
              </v-row>
            </div>

            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                @click="dialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="progressing"
                color="success"
                @click="submitForm"
              >
                Update
              </v-btn>
            </v-card-actions>
          </div>
        </v-container>
      </v-form>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    inject: ['$validator'],
    data () {
      return {
        companyName: '',
        dialog: false,
        loading: false,
        progressing: false,
        searched: false,
        companyId: '',
        year: '',
        companyQuotas: [],
        companies: [],
        quotaCategory: '',
        selectedCompanyQuota: [],
        localCompanyQuota: '',
      }
    },
    computed: {
      years () {
        const start = this.$moment().year()
        const end = this.$moment().year() + 1
        return [start.toString(), end.toString()]
      },
    },
    watch: {
      dialog (value) {
        if (value) {
          this.loadData()
        } else {
          this.$resetData()
        }
      },
    },
    mounted () {
      const scope = this

      this.$validator.extend('quotaLimit', {
        getMessage (field, args) {
          return 'The value will exceeds the quota limit'
        },
        validate (value, object) {
          return scope.isWithinLimits(object)
        },
      })
    },
    methods: {
      loadData () {
        this.loading = true
        this.$api.companies().list()
          .then((response) => {
            this.companies = response.data
            this.loading = false
          })

        this.$api.companyQuotaCategories().list()
          .then((response) => {
            this.categories = response.data
            this.loading = false
          })
      },
      createAndEdit () {
        this.progressing = true
        this.$api.companyQuotas().create(this.simpleFormData())
          .then((response) => {
            if (response.status === 201) {
              this.filterQuota()
            }
          }).catch(err => {
            this.$snack({ color: 'error', message: err.response.statusText })
          })
      },
      simpleFormData () {
        return {
          company_id: this.companyId,
          year: this.year,
          category_id: this.quotaCategory,
        }
      },
      filterQuota () {
        this.$api.companyQuotas().list(this.companyId, this.year, this.quotaCategory)
          .then((response) => {
            const companyQuotas = response.data
            if (companyQuotas.length !== 0) {
              this.companyQuotas = companyQuotas
              const company = this.companies.filter((company) => {
                return company.id === this.companyId
              })
              this.companyName = company[0].name
              this.quotaCategory = this.quotaCategory ? companyQuotas[0].company_quota_category.value : null
              this.companyQuotas.sort((a, b) => {
                return a.date < b.date ? -1 : 1
              })
              this.selectedCompanyQuota = this.companyQuotas.filter((companyQuota) => {
                return companyQuota.company_id === this.companyId && companyQuota.year === this.year
              })
              this.localCompanyQuota = [...this.selectedCompanyQuota]
            }
            this.progressing = false
            this.searched = true
          })
      },
      months (value) {
        return this.$moment().month(value - 1).format('MMMM') + ' Quota'
      },
      isPast (value) {
        return this.$moment().format('YYYY-MM') > this.$moment(value, 'YYYY-MM-DD').format('YYYY-MM')
      },
      isWithinLimits (object) {
        const startDate = this.$moment(object.date, 'YYYY-MM-DD').subtract(11, 'months').format('YYYY-MM-DD')
        const pastCompanyQuota = this.companyQuotas.filter((companyQuota) => {
          return this.$moment(companyQuota.date, 'YYYY-MM-DD') >= this.$moment(startDate, 'YYYY-MM-DD') &&
            this.$moment(companyQuota.date, 'YYYY-MM-DD') < this.$moment()
        })
        const pastTotal = pastCompanyQuota.reduce((total, companyQuota) => total + companyQuota.quota_used, 0)

        const projectedCompanyQuota = this.localCompanyQuota.filter((companyQuota) => {
          return this.$moment(companyQuota.date, 'YYYY-MM-DD').format('YYYY-MM') >= this.$moment().format('YYYY-MM') && 
            this.$moment(companyQuota.date, 'YYYY-MM-DD').format('YYYY-MM') <= this.$moment(object.date, 'YYYY-MM-DD').format('YYYY-MM')
        })
        const projectedTotal = projectedCompanyQuota.reduce((total, companyQuota) => total + parseFloat(companyQuota.quota), 0)
        return pastTotal + projectedTotal < 500000
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.progressing = true
            this.$api.companyQuotas().updateMany(this.companyId, this.prepareFormData())
              .then((response) => {
                if (response.status === 200) {
                  this.$emit('updated')
                  this.$snack({ color: 'success', message: 'Company Quota updated successfully' })
                  this.dialog = false
                }
              }).catch(err => {
                let errorMsg = ''
                if (err.response.data.errors) {
                  const errors = err.response.data.errors
                  console.log(errors)
                  for (const error in errors) { 
                    errorMsg = errorMsg + errors[error][0] + ' \n'
                  }
                } else {
                  errorMsg = err.response.statusText
                }
                console.log(err)
                this.$snack({ color: 'error', message: errorMsg })
              }).then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        const quotas = []
        const projectedCompanyQuota = this.localCompanyQuota.filter((companyQuota) => {
          return this.$moment(companyQuota.date, 'YYYY-MM-DD').format('YYYY-MM-DD') >= this.$moment().startOf('month').format('YYYY-MM-DD')
        })
        projectedCompanyQuota.map((companyQuota) => {
          const tempQuota = {
            id: companyQuota.id,
            month: companyQuota.month,
            quota: companyQuota.quota,
          }
          quotas.push(tempQuota)
        })
        return {
          year: this.year,
          quotas: quotas,
        }
      },
    },
  }
</script>
<style lang="css" scoped>
  .overflow {
    height: 60vh;
    overflow-x: hidden;
  }

  .simplebar-track.simplebar-vertical .simplebar-scrollbar:before {
    background-image: linear-gradient( grey , grey);
  }
</style>
